import { Controller } from "@hotwired/stimulus"

// Client-side pagination and sorting for review list
// All reviews are loaded upfront and paginated/sorted in JavaScript
export default class extends Controller {
  static targets = ["reviewsData", "list", "pagination", "paginationInfo", "sortSelect"]
  static values = {
    perPage: { type: Number, default: 15 },
    page: { type: Number, default: 1 },
    sort: { type: String, default: "newest" }
  }

  connect() {
    this.reviews = JSON.parse(this.reviewsDataTarget.textContent)
    this.render()
  }

  sort(event) {
    this.sortValue = event.target.value
    this.pageValue = 1
    this.render()
  }

  goToPage(event) {
    event.preventDefault()
    const page = parseInt(event.currentTarget.dataset.page, 10)
    if (page && page !== this.pageValue) {
      this.pageValue = page
      this.render()
      this.scrollToTop()
    }
  }

  previousPage(event) {
    event.preventDefault()
    if (this.pageValue > 1) {
      this.pageValue--
      this.render()
      this.scrollToTop()
    }
  }

  nextPage(event) {
    event.preventDefault()
    const totalPages = Math.ceil(this.reviews.length / this.perPageValue)
    if (this.pageValue < totalPages) {
      this.pageValue++
      this.render()
      this.scrollToTop()
    }
  }

  scrollToTop() {
    const elementTop = this.element.getBoundingClientRect().top + window.pageYOffset
    const offset = 100
    window.scrollTo({ top: elementTop - offset, behavior: 'smooth' })
  }

  render() {
    const sorted = this.sortReviews([...this.reviews])
    const paginated = this.paginateReviews(sorted)

    this.renderReviews(paginated)
    this.renderPagination()
  }

  sortReviews(reviews) {
    switch (this.sortValue) {
      case 'oldest':
        return reviews.sort((a, b) => new Date(a.reviewDate) - new Date(b.reviewDate))
      case 'highest':
        return reviews.sort((a, b) => b.rating - a.rating || new Date(b.reviewDate) - new Date(a.reviewDate))
      case 'lowest':
        return reviews.sort((a, b) => a.rating - b.rating || new Date(b.reviewDate) - new Date(a.reviewDate))
      case 'newest':
      default:
        return reviews.sort((a, b) => new Date(b.reviewDate) - new Date(a.reviewDate))
    }
  }

  paginateReviews(reviews) {
    const start = (this.pageValue - 1) * this.perPageValue
    const end = start + this.perPageValue
    return reviews.slice(start, end)
  }

  renderReviews(reviews) {
    if (reviews.length === 0 && this.reviews.length === 0) {
      this.listTarget.innerHTML = `
        <div class="text-center py-5 text-muted">
          <i class="fa-solid fa-star fa-2x mb-3" style="color: #ddd;"></i>
          <p class="mb-0">No reviews yet. Be the first to write a review!</p>
        </div>
      `
      return
    }
    this.listTarget.innerHTML = reviews.map(review => this.renderReview(review)).join('')
  }

  renderReview(review) {
    const stars = this.renderStars(review.rating)
    const location = review.authorLocation || ''
    const dateFormatted = review.reviewDateFormatted || ''
    const timeAgo = review.timeAgo || ''

    return `
      <article class="review-item py-2 border-bottom">
        <div class="row">
          <div class="col-12 col-md-3 mb-2 mb-md-0">
            <div class="fw-bold small">${this.escapeHtml(review.authorName || 'Customer')}</div>
            ${location ? `<div class="text-muted small">${this.escapeHtml(location)}</div>` : ''}
            <div class="text-muted small">
              <i class="fa-solid fa-circle-check text-success me-1"></i>
              Verified Customer
            </div>
          </div>
          <div class="col-12 col-md-9">
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-1">
              <div class="stars" style="color: #577d3c; letter-spacing: -0.05rem; font-size: 0.9rem;">
                ${stars}
              </div>
              ${dateFormatted ? `
                <div class="text-muted small">
                  ${this.escapeHtml(dateFormatted)}
                  ${timeAgo ? `<span class="ms-1">(${this.escapeHtml(timeAgo)})</span>` : ''}
                </div>
              ` : ''}
            </div>
            ${review.itemName ? `
              <div class="fst-italic text-muted small mb-1">
                <i class="fa-solid fa-box me-1"></i>
                ${this.escapeHtml(review.itemName)}
              </div>
            ` : ''}
            ${review.title ? `<div class="fw-bold small mb-1">${this.escapeHtml(review.title)}</div>` : ''}
            ${review.comments ? `<p class="mb-2 small" style="line-height: 1.5;">${this.escapeHtml(review.comments)}</p>` : ''}
            ${this.renderPhotos(review.photos)}
            ${this.renderTags(review.tags)}
            ${this.renderReplies(review.replies)}
          </div>
        </div>
      </article>
    `
  }

  renderStars(rating) {
    const filled = Math.round(rating || 0)
    const empty = 5 - filled
    let html = ''
    for (let i = 0; i < filled; i++) {
      html += '<i class="fa-solid fa-star"></i>'
    }
    for (let i = 0; i < empty; i++) {
      html += '<i class="fa-regular fa-star"></i>'
    }
    return html
  }

  renderPhotos(photos) {
    if (!photos || photos.length === 0) return ''

    const photoHtml = photos.map(photo => {
      const url = typeof photo === 'object' ? photo.url : photo
      if (!url) return ''
      return `
        <a href="${this.escapeHtml(url)}" data-fancybox="review-photos">
          <img src="${this.escapeHtml(url)}" alt="Review photo" class="rounded" style="height: 60px; width: auto; object-fit: cover;">
        </a>
      `
    }).join('')

    return photoHtml ? `<div class="review-photos d-flex flex-wrap gap-2 mb-2">${photoHtml}</div>` : ''
  }

  renderTags(tags) {
    if (!tags || tags.length === 0) return ''

    const tagHtml = tags.map(tag => {
      const text = typeof tag === 'object' ? tag.tag : tag
      if (!text) return ''
      return `<span class="badge bg-light text-muted">${this.escapeHtml(text)}</span>`
    }).join('')

    return tagHtml ? `<div class="review-tags d-flex flex-wrap gap-1 mb-2">${tagHtml}</div>` : ''
  }

  renderReplies(replies) {
    if (!replies || replies.length === 0) return ''

    const replyHtml = replies.map(reply => {
      const author = typeof reply === 'object' ? (reply.author || 'WarmlyYours') : 'WarmlyYours'
      const comment = typeof reply === 'object' ? reply.comment : reply
      const date = typeof reply === 'object' ? reply.date : ''

      return `
        <div class="reply bg-light rounded p-2 ms-3 border-start border-2 border-primary">
          <div class="d-flex align-items-center gap-2 mb-1">
            <i class="fa-solid fa-reply text-muted small"></i>
            <span class="fw-bold small">${this.escapeHtml(author)}</span>
            ${date ? `<span class="text-muted small">${this.escapeHtml(date)}</span>` : ''}
          </div>
          <p class="mb-0 small">${this.escapeHtml(comment || '')}</p>
        </div>
      `
    }).join('')

    return replyHtml ? `<div class="review-replies mt-2">${replyHtml}</div>` : ''
  }

  renderPagination() {
    const total = this.reviews.length
    const totalPages = Math.ceil(total / this.perPageValue)
    const current = this.pageValue

    if (totalPages <= 1) {
      this.paginationTarget.innerHTML = ''
      this.paginationInfoTarget.innerHTML = ''
      return
    }

    const pages = this.pageWindow(current, totalPages)

    let paginationHtml = `
      <ul class="pagination mb-2">
        <li class="page-item ${current === 1 ? 'disabled' : ''}">
          <a href="#" class="page-link" data-action="click->review-list#previousPage" ${current === 1 ? 'tabindex="-1" aria-disabled="true"' : ''}>
            ‹ Previous
          </a>
        </li>
    `

    pages.forEach(pg => {
      if (pg === 'gap') {
        paginationHtml += `<li class="page-item disabled"><span class="page-link">…</span></li>`
      } else {
        paginationHtml += `
          <li class="page-item ${pg === current ? 'active' : ''}">
            <a href="#" class="page-link" data-action="click->review-list#goToPage" data-page="${pg}">${pg}</a>
          </li>
        `
      }
    })

    paginationHtml += `
        <li class="page-item ${current === totalPages ? 'disabled' : ''}">
          <a href="#" class="page-link" data-action="click->review-list#nextPage" ${current === totalPages ? 'tabindex="-1" aria-disabled="true"' : ''}>
            Next ›
          </a>
        </li>
      </ul>
    `

    this.paginationTarget.innerHTML = paginationHtml

    const from = (current - 1) * this.perPageValue + 1
    const to = Math.min(current * this.perPageValue, total)
    this.paginationInfoTarget.innerHTML = `Showing ${from}-${to} of ${total.toLocaleString()} reviews`
  }

  pageWindow(current, total, window = 2) {
    if (total <= (window * 2) + 5) {
      return Array.from({ length: total }, (_, i) => i + 1)
    }

    const pages = []
    pages.push(1)

    if (current - window > 2) pages.push('gap')

    for (let pg = current - window; pg <= current + window; pg++) {
      if (pg > 1 && pg < total) pages.push(pg)
    }

    if (current + window < total - 1) pages.push('gap')

    pages.push(total)

    return [...new Set(pages)]
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
